/* eslint-disable  @typescript-eslint/no-explicit-any */
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { useLazyQuery } from '@apollo/client';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Collapse, List, ListItemButton, ListItemIcon, ListItemText, Typography } from '@mui/material';

// project imports
import NavItem from '../NavItem';

// assets
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { IconChevronDown, IconChevronUp } from '@tabler/icons';

import { ALL_PROFILES } from 'graphql/profiles/profiles.query';
import { getUser } from 'utils/user';
import { ProfileData } from 'views/profiles/types/profile.types';

// ==============================|| SIDEBAR MENU LIST COLLAPSE ITEMS ||============================== //

const NavCollapse = ({ menu, level }: any) => {
  const user = getUser();

  const theme: any = useTheme();
  const customization = useSelector((state: any) => state.customization);

  const [load, { loading, data }] = useLazyQuery(ALL_PROFILES);

  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [profiles, setProfiles] = useState<ProfileData[]>([]);

  const rolePermissions = profiles.find((profile) => profile.name === user.role);
  const permissions: Record<string, string[] | undefined> = rolePermissions?.permissions || ({} as Record<string, string[]>);

  const handleClick = () => {
    setOpen(!open);
    setSelected(!selected ? menu.id : null);
  };

  const { pathname } = useLocation();

  const normalizePath = (value = '') => {
    const path = `/${value}`.replace(/\/+/g, '/');
    if (path === '/') return path;
    return path.endsWith('/') ? path.slice(0, -1) : path;
  };

  const isPathMatch = (currentPath: string, menuUrl = '') => {
    const menuPath = normalizePath(menuUrl);
    if (!menuPath) return false;
    if (currentPath === menuPath) return true;
    return currentPath.startsWith(`${menuPath}/`);
  };

  const hasActiveChild = (children: any[], currentPath: string): boolean => {
    return children.some((item: any) => {
      if (item.children?.length) {
        return hasActiveChild(item.children, currentPath);
      }

      return isPathMatch(currentPath, item.url);
    });
  };

  useEffect(() => {
    let mounted = true;

    if (mounted) {
      load();
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return () => (mounted = false as any);
  }, [load]);

  useEffect(() => {
    if (data) {
      setProfiles(data.allProfiles.result);
    }
  }, [data]);

  // menu collapse for sub-levels
  useEffect(() => {
    const currentPath = normalizePath(pathname);
    const hasActiveRoute = menu.children ? hasActiveChild(menu.children, currentPath) : false;

    setOpen(hasActiveRoute);
    setSelected(hasActiveRoute ? menu.id : null);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, menu.children]);

  // menu collapse & item
  const menus = menu.children
    .filter((item: any) => {
      const [page, action] = item.id.split('-');

      return permissions[page] && permissions[page]?.includes(action);
    })
    ?.map((item: any) => {
      if (item.type === 'collapse') {
        return <NavCollapse key={item.id} menu={item} level={level + 1} />;
      }

      if (item.type === 'item') {
        return <NavItem key={item.id} item={item} level={level + 1} />;
      }

      return (
        <Typography key={item.id} variant="h6" color="error" align="center">
          Menu Items Error
        </Typography>
      );
    });

  const Icon = menu.icon;
  const menuIcon = menu.icon ? (
    <Icon strokeWidth={1.5} size="1.3rem" style={{ marginTop: 'auto', marginBottom: 'auto' }} />
  ) : (
    <FiberManualRecordIcon
      sx={{
        width: selected === menu.id ? 8 : 6,
        height: selected === menu.id ? 8 : 6
      }}
      fontSize={level > 0 ? 'inherit' : 'medium'}
    />
  );

  if (loading) return <></>;

  return (
    <>
      <ListItemButton
        sx={{
          borderRadius: `${customization.borderRadius}px`,
          mb: 0.5,
          alignItems: 'flex-start',
          backgroundColor: level > 1 ? 'transparent !important' : 'inherit',
          py: level > 1 ? 1 : 1.25,
          pl: `${level * 24}px`
        }}
        selected={selected === menu.id}
        onClick={handleClick}
      >
        <ListItemIcon sx={{ my: 'auto', minWidth: !menu.icon ? 18 : 36 }}>{menuIcon}</ListItemIcon>
        <ListItemText
          primary={
            <Typography variant={selected === menu.id ? 'h5' : 'body1'} color="inherit" sx={{ my: 'auto' }}>
              {menu.title}
            </Typography>
          }
          secondary={
            menu.caption && (
              <Typography variant="caption" sx={{ ...theme.typography.subMenuCaption }} display="block" gutterBottom>
                {menu.caption}
              </Typography>
            )
          }
        />
        {open ? (
          <IconChevronUp stroke={1.5} size="1rem" style={{ marginTop: 'auto', marginBottom: 'auto' }} />
        ) : (
          <IconChevronDown stroke={1.5} size="1rem" style={{ marginTop: 'auto', marginBottom: 'auto' }} />
        )}
      </ListItemButton>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <List
          component="div"
          disablePadding
          sx={{
            position: 'relative',
            '&:after': {
              content: "''",
              position: 'absolute',
              left: '32px',
              top: 0,
              height: '100%',
              width: '1px',
              opacity: 1,
              background: theme.palette.primary.light
            }
          }}
        >
          {menus}
        </List>
      </Collapse>
    </>
  );
};

NavCollapse.propTypes = {
  menu: PropTypes.object,
  level: PropTypes.number
};

export default NavCollapse;
